/**
 * Combo — 단일 선택 드롭다운 (Figma 765-7708: Label + ChevronDown 트리거)
 * Property variant (CVA): size 36px | 28px, disabled, state (default/hovered/focused/pressed)
 */

import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/styles/ui/select";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// ============================================
// 타입 정의
// ============================================

export interface ComboOption {
  value: string;
  label: string;
}

// ============================================
// 트리거 스타일 (CVA) — Figma Button/Combo (Transparent)
// 36px: Label섹션 px=10 py=8 / Chevron섹션 px=4 py=8 / radius=6
// 28px: Label섹션 px=8  py=4 / Chevron섹션 px=4 py=4 / radius=6
// Label과 chevron 사이 세로 구분선(Line/Normal) 포함
// ============================================

const comboTriggerVariants = cva("", {
  variants: {
    size: {
      "36px":
        "h-9 p-0 gap-0 rounded-[6px] body-2-normal-medium [&>span]:px-2.5 [&>span]:py-2 [&_svg]:size-4 [&>svg]:mx-1 [&>svg]:my-2 before:right-[24px]",
      "28px":
        "h-7 p-0 gap-0 rounded-[6px] label-2-medium [&>span]:px-2 [&>span]:py-1 [&_svg]:size-3.5 [&>svg]:mx-1 [&>svg]:my-1 before:right-[22px]",
    },
    disabled: {
      true: [
        "disabled:cursor-not-allowed disabled:!text-label-disable",
        // Figma disabled: 외곽 border + 구분선 Line/Neutral + 인터랙션 제거
        "disabled:border disabled:border-line-neutral",
        "disabled:before:!bg-line-normal",
        "disabled:hover:bg-transparent disabled:active:bg-transparent disabled:focus:bg-transparent",
      ].join(" "),
      false: "",
    },
  },
  defaultVariants: {
    size: "36px",
    disabled: false,
  },
});

const comboItemVariants = cva("", {
  variants: {
    size: {
      "36px": "body-2-normal-medium",
      "28px": "label-2-medium",
    },
  },
  defaultVariants: {
    size: "36px",
  },
});

export type ComboSize = NonNullable<
  VariantProps<typeof comboTriggerVariants>["size"]
>;

export interface ComboProps extends VariantProps<typeof comboTriggerVariants> {
  /** 선택된 값 (options의 value와 매칭) */
  value?: string | null;
  /** 값 변경 콜백 */
  onValueChange?: (value: string) => void;
  /** 옵션 목록 */
  options: ComboOption[];
  /** 미선택 시 트리거에 표시할 문구 (Figma 기본 "Label") */
  placeholder?: string;
  /** 트리거 루트 className */
  className?: string;
  /** 콘텐츠(드롭다운) className */
  contentClassName?: string;
}

// ============================================
// Combo 컴포넌트
// ============================================

export const ComboButton = ({
  value,
  onValueChange,
  options,
  placeholder = "Label",
  size = "36px",
  disabled = false,
  className,
  contentClassName,
}: ComboProps) => {
  return (
    <Select
      value={value || undefined}
      onValueChange={onValueChange}
      disabled={disabled === true}
    >
      <SelectTrigger
        className={cn(
          [
            "relative inline-flex items-center justify-between w-full min-w-0 text-left transition-colors cursor-pointer focus:outline-none",
            "[&>span]:flex-1 [&>span]:min-w-0 [&>span]:line-clamp-1 [&>span]:truncate [&_svg]:shrink-0 [&_svg]:opacity-100",
            "bg-white text-label-normal border-0 shadow-none box-border",
            "data-[placeholder]:text-muted-foreground",
            // Figma Decorate/Interaction/Normal/Neutral: hover 8 / focus 12 / pressed 16
            "hover:bg-neutral-950/8",
            "focus-visible:bg-neutral-950/12",
            "active:bg-neutral-950/16",
            "data-[state=open]:bg-neutral-950/12",
            // Focus: Label/Normal inset ring (Figma focused border)
            "focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-label-normal",
            "data-[state=open]:ring-1 data-[state=open]:ring-inset data-[state=open]:ring-label-normal",
            // Label과 chevron 사이 세로 구분선 (기본 white, disabled일 때만 line-neutral)
            "before:content-[''] before:absolute before:w-px before:bg-white before:h-full before:top-0",
          ].join(" "),
          comboTriggerVariants({ size, disabled: Boolean(disabled) }),
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            className={cn(comboItemVariants({ size }))}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

ComboButton.displayName = "Combo";

export default ComboButton;
